import { useState, useEffect, useCallback } from "react";
import { getNotifications, markAllNotificationsRead, markNotificationRead } from "@/api";
import { Bell, CheckCheck, Info, AlertCircle, CheckCircle, AlertTriangle, X, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "sonner";

export default function NotificationPanel() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [sendingTest, setSendingTest] = useState(false);
  const { requestPermission, subscribeToPush, sendTestPush } = usePushNotifications();

  const load = useCallback(async () => {
    try {
      const res = await getNotifications();
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    load();
  };

  const handleMarkRead = async (id) => {
    await markNotificationRead(id);
    load();
  };

  const handleTestPush = async () => {
    setSendingTest(true);
    try {
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) {
          toast.error('Please allow notifications in your browser first');
          setSendingTest(false);
          return;
        }
      }
      const sub = await subscribeToPush();
      if (!sub.success) {
        const reason = sub.reason || 'unknown';
        if (reason === 'unsupported') {
          toast.error('Your browser does not support push notifications');
        } else if (reason === 'vapid-not-configured') {
          toast.error('Push not configured on server');
        } else {
          toast.error(`Could not subscribe: ${reason}`);
        }
        setSendingTest(false);
        return;
      }
      const res = await sendTestPush();
      if (res?.sent > 0) {
        toast.success(`Test push sent to ${res.sent} device(s)!`);
      } else {
        toast.warning('Subscribed but no devices received the test. Try reloading and retrying.');
      }
    } catch (err) {
      toast.error('Failed to send test push');
    } finally {
      setSendingTest(false);
    }
  };

  const iconMap = {
    info: <Info className="h-4 w-4 text-[#1565C0]" />,
    success: <CheckCircle className="h-4 w-4 text-[#10B981]" />,
    warning: <AlertTriangle className="h-4 w-4 text-[#F59E0B]" />,
    error: <AlertCircle className="h-4 w-4 text-[#EF4444]" />,
  };

  const timeAgo = (date) => {
    const now = new Date();
    const d = new Date(date);
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className="p-2 text-[#475569] hover:text-[#0F172A] relative"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-[#EF4444] text-white text-[9px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-[70vh] flex flex-col" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-[#0F172A]" style={{fontFamily:'Outfit'}}>Notifications</h3>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleTestPush}
                  disabled={sendingTest}
                  data-testid="test-push-btn"
                  className="text-xs text-[#1565C0] h-7 px-2"
                  title="Send a test push notification to verify it works"
                >
                  <BellRing className="h-3 w-3 mr-1" /> {sendingTest ? '...' : 'Test'}
                </Button>
                {unread > 0 && (
                  <Button size="sm" variant="ghost" onClick={handleMarkAllRead} data-testid="mark-all-read" className="text-xs text-[#1565C0] h-7 px-2">
                    <CheckCheck className="h-3 w-3 mr-1" /> Mark all read
                  </Button>
                )}
                <button onClick={() => setOpen(false)} className="text-[#475569] hover:text-[#0F172A] p-1">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-[#475569]">No notifications yet</p>
                </div>
              ) : (
                notifications.map(n => (
                  <div
                    key={n.id}
                    onClick={() => !n.read && handleMarkRead(n.id)}
                    className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${!n.read ? 'bg-[#1565C0]/[0.03]' : ''}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0">{iconMap[n.type] || iconMap.info}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm ${!n.read ? 'font-semibold text-[#0F172A]' : 'font-medium text-[#475569]'}`}>{n.title}</p>
                          {!n.read && <div className="w-2 h-2 bg-[#1565C0] rounded-full flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-[#475569] mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
